import { Star } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { useFavoritoProcesso } from '@/hooks/useFavoritoProcesso';
import { usePodeColaborar } from '@/lib/delegacao/atuacao';
import { cn } from '@/lib/utils';

interface FavoritoProcessoButtonProps {
  processoId: string;
  favorito: boolean;
  className?: string;
}

/**
 * Estrela do "acompanho de perto" — no Acervo (linha e card) e no cabeçalho dos autos.
 *
 * PREENCHIDA quando marcada, não só colorida: cor sozinha não basta para quem não a
 * distingue, e a estrela cheia se lê de longe. O clique não vaza para a linha/card
 * (que abre os autos).
 *
 * Delegação em Leitura não marca (a rota exige Colaboração): sem botão; a marcação do
 * titular continua visível como estrela estática.
 */
export function FavoritoProcessoButton({ processoId, favorito, className }: FavoritoProcessoButtonProps) {
  const podeColaborar = usePodeColaborar();
  const { definir } = useFavoritoProcesso();

  if (!podeColaborar) {
    if (!favorito) return null;
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <span
            role="img"
            aria-label="Acompanhado de perto"
            className="inline-flex shrink-0 items-center justify-center text-amber-500"
          >
            <Star className="h-4 w-4 fill-current" aria-hidden />
          </span>
        </TooltipTrigger>
        <TooltipContent>Acompanhado de perto</TooltipContent>
      </Tooltip>
    );
  }

  const rotulo = favorito ? 'Deixar de acompanhar' : 'Acompanhar de perto';
  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <Button
          variant="ghost"
          size="icon"
          aria-label={rotulo}
          aria-pressed={favorito}
          onClick={(e) => {
            e.stopPropagation();
            void definir(processoId, !favorito);
          }}
          className={cn(
            // Alvo de toque de 44px sem crescer o ícone: a área extra é o `::after`
            // em volta (a raiz tem 15px; h-8 dá 30px, +2×7px = 44px).
            "relative h-8 w-8 shrink-0 after:absolute after:-inset-[7px] after:content-['']",
            favorito ? 'text-amber-500 hover:text-amber-600' : 'text-muted-foreground',
            className,
          )}
        >
          <Star className={cn('h-4 w-4', favorito && 'fill-current')} aria-hidden />
        </Button>
      </TooltipTrigger>
      <TooltipContent>{rotulo}</TooltipContent>
    </Tooltip>
  );
}
